import React, { useEffect, useState } from 'react';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';

export function formatNumberScale(number, usd = false, decimals = 1) {
  if (isNaN(number) || number === '' || number === undefined) {
    return usd ? '$0.00' : '0'
  }
  const num = parseFloat(number)
  const fullNum = Math.floor(num).toLocaleString('fullwide', { useGrouping: false })
  const wholeNumberLength = fullNum.length

  if (wholeNumberLength >= 19) return usd ? '> $1000 Q' : '> 1000 Q'
  if (wholeNumberLength >= 16) return (usd ? '$' : '') + (num / Math.pow(10, 15)).toFixed(decimals) + ' Q'
  if (wholeNumberLength >= 13) return (usd ? '$' : '') + (num / Math.pow(10, 12)).toFixed(decimals) + ' T'
  if (wholeNumberLength >= 10) return (usd ? '$' : '') + (num / Math.pow(10, 9)).toFixed(decimals) + ' B'
  if (wholeNumberLength >= 7) return (usd ? '$' : '') + (num / Math.pow(10, 6)).toFixed(decimals) + ' M'
  if (wholeNumberLength >= 4) return (usd ? '$' : '') + (num / Math.pow(10, 3)).toFixed(decimals) + ' K'

  if (num < 0.0001 && num > 0) {
    return usd ? '< $0.0001' : '< 0.0001'
  }

  return (usd ? '$' : '') + num.toFixed(decimals)
}

export function formatNumberScale2(number, usd = false, decimals = 2) {
  if (isNaN(number) || number === '' || number === undefined) {
    return usd ? '$0.00' : '0'
  }
  decimals = 1
  const num = parseFloat(number)
  const fullNum = Math.floor(num).toString() // Get the integer part of the number
  const wholeNumberLength = fullNum.length

  if (wholeNumberLength >= 19) return usd ? '> $1000 Q' : '> 1000 Q'
  if (wholeNumberLength >= 16) return (usd ? '$' : '') + (num / Math.pow(10, 15)).toFixed(decimals) + ' Q'
  if (wholeNumberLength >= 13) return (usd ? '$' : '') + (num / Math.pow(10, 12)).toFixed(decimals) + ' T'
  if (wholeNumberLength >= 10) return (usd ? '$' : '') + (num / Math.pow(10, 9)).toFixed(decimals) + ' B'
  if (wholeNumberLength >= 7) return (usd ? '$' : '') + (num / Math.pow(10, 6)).toFixed(decimals) + ' M'
  if (wholeNumberLength >= 4) return (usd ? '$' : '') + (num / Math.pow(10, 3)).toFixed(decimals) + ' K'

  if (num < 0.0001 && num > 0) {
    return usd ? '< $0.0001' : '< 0.0001'
  }

  return (usd ? '$' : '') + num.toFixed(1)
}

const BAR_COLORS = ['#48cae4', '#0077b6', '#33C0A4', '#A8DCD9', '#ECA089']
const LINE_COLOR = '#308089'
const LEFT_YTICK_COLOR = '#FFE5FF'

const StackedBarChart = ({
  data,
  xDataKey,
  yDataKeys,
  legend,
  colors,
  cumulative,
  format,
}) => {
  const [chartData, setChartData] = useState([])

  if (colors === undefined) {
    colors = BAR_COLORS
  }

  useEffect(() => {
    if (!data.length) {
      return
    }

    if (!cumulative) {
      setChartData(data)
      return
    }

    let updatedData = [...data]
    for (let i = 0; i < updatedData.length; i++) {
      let cumulative = yDataKeys.reduce(
        (prev, currKey) => prev + updatedData[i][currKey],
        0
      )
      if (i !== 0) {
        cumulative += updatedData[i - 1].cumulative
      }

      updatedData[i].cumulative = Number(cumulative.toFixed(4))
    }

    setChartData(updatedData)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  return (
    <ResponsiveContainer width="95%" height={400}>
      <ComposedChart data={chartData} debounce={1}>
        <Legend layout="horizontal" verticalAlign="bottom" align="center" />
        {yDataKeys.map((key, index) => (
          <Bar
            name={legend[index]}
            key={key}
            dataKey={key}
            fill={colors[index]}
            stackId="a"
          />
        ))}
        <CartesianGrid
          horizontal={true}
          vertical={true}
          opacity={0.2}
          strokeDasharray="8"
        />
        <XAxis dataKey={xDataKey} />
        <YAxis
          // domain={[0, maxVal]}
          label={{ fill: LEFT_YTICK_COLOR }}
          tick={{ stroke: LEFT_YTICK_COLOR }}
          tickLine={{ stroke: LEFT_YTICK_COLOR }}
          fontSize="14px"
          tickFormatter={
            format === false
              ? (data) => {
                return data
              }
              : formatNumberScale
          }
        />
        {cumulative && (
          <YAxis
            yAxisId="right"
            // domain={[0, maxCumulativeVal]}
            orientation="right"
            label={{ fill: LINE_COLOR }}
            tick={{ stroke: LINE_COLOR }}
            tickLine={{ stroke: LINE_COLOR }}
          />
        )}
        {cumulative && (
          <Line
            name="Cumulative"
            type="monotone"
            dataKey="cumulative"
            stroke={LINE_COLOR}
            strokeWidth={2}
            dot={false}
            yAxisId="right"
          />
        )}
        <Tooltip
          contentStyle={{
            backgroundColor: '#05113b',
            border: 'none',
            borderRadius: '12px',
            fontSize: '12px',
          }}
          formatter={
            format === false
              ? (data) => {
                return data
              }
              : formatNumberScale2
          }
        />
      </ComposedChart>
    </ResponsiveContainer>
  )
}

export default StackedBarChart
